// Types
import Alt from '@/types/models/alt';
import AppRegion from '@/types/app-region';
import Benchmark from '@/types/benchmark';
import ComparisonItem from '@/types/comparison-item';
import Dictionary from '@/types/dictionary';
import Fund from '@/types/fund';
import Holding from '@/types/holding';
import Stock from '@/types/stock';

// Utils
import altToComparisonItem from './alt-to-comparison-item';
import fundToComparisonItem from './fund-to-comparison-item';
import stockToComparisonItem from './stock-to-comparison-item';
import { HOLDING_TYPES } from '@/utils/portfolios';

/**
 * Convert multiple holdings into comparison items.
 * @param holdings Holdings.
 * @param data Dictionary of holdings data.
 * @return Resulting comparison items.
 */
const holdingsToComparisonItems = (
  holdings: { id: string; symbol?: string; type: string }[],
  data: Dictionary<Holding>,
  appRegion: AppRegion,
  benchmarks?: Benchmark[],
) =>
  holdings.reduce((acc, holding) => {
    const itemData = data[holding.id];
    if (!itemData) {
      return acc;
    }

    let comparisonItem: ComparisonItem | undefined;
    switch (holding.type) {
      case HOLDING_TYPES.ALT: {
        comparisonItem = altToComparisonItem(itemData as Alt);
        break;
      }
      case HOLDING_TYPES.FUND:
      case HOLDING_TYPES.CUSTOM_YIELD: {
        comparisonItem = fundToComparisonItem(itemData as Fund, appRegion, benchmarks);
        break;
      }
      case HOLDING_TYPES.STOCK: {
        comparisonItem = stockToComparisonItem(itemData as Stock, benchmarks, undefined, appRegion);
        break;
      }
    }

    if (comparisonItem) {
      // Ensure that the comparison item produced has the holding's symbol rather than
      // the first item of the symbol's array
      return [
        ...acc,
        {
          ...comparisonItem,
          holdings: [
            {
              ...comparisonItem.holdings[0],
              symbol: holding.symbol,
            },
          ],
        },
      ];
    }

    return acc;
  }, [] as ComparisonItem[]);

export default holdingsToComparisonItems;
